// client/src/utils/lineItemFlags.ts
//
// Per-cell review flags for the vendor-invoice review screen.
//
// There is no per-field confidence anywhere in the pipeline — `extracted.confidence`
// is a single invoice-level 0..1 score. So rather than invent one, this derives
// per-cell flags from two kinds of signal:
//
//   1. Provenance the processor already records but never surfaced: VAT
//      provenance (taxRateDefaulted / taxRateUnresolved / taxRateSuggested) and
//      OCR unit repairs.
//   2. Arithmetic recomputed HERE, from the values currently on screen.
//
// (2) is why the server's `extracted.validation` is no longer the source of
// truth for the grid. That block is written once, at processing time, and never
// again until a reprocess — so it could neither see a gap the parser left nor a
// figure a user typed. Two consequences, both reported from the floor:
// corrections stayed red after they were fixed, and a quantity typed in by hand
// (10000 against a $10 line) raised nothing at all, because nothing re-checked
// the line against the invoice's own totals. Everything below recomputes on
// every render, so a flag appears the moment a value stops adding up and clears
// the moment it does.
//
// Nothing here is persisted: a flag is a statement about the values as they are
// right now, and stored state can only go stale against them.

import { isKnownUnit, readUnitNotation, slotTypeIssues, unitFromSizeText } from "./unitVocabulary";
import {
  deriveDueDate,
  formatDdMmYyyy,
  invoiceDateChecks,
  toInvoiceDate,
  FUTURE_DATE_TOLERANCE_DAYS,
} from "./paymentTerms";
import { formatDateSpelled } from "./dateFormat";
// The arithmetic itself now lives in invoiceLineMath, which has a line-for-line
// twin at src/utils/invoiceLineMath.js. It used to be written out here AND again
// in the server's vendorInvoiceProcessor, and the two disagreed about the
// overcharge figure — see that module's header for the case they differed on.
import {
  tolerance,
  num,
  money,
  isEmptyValue,
  round2,
  computeLineMath,
  matchesAny,
  invoiceConvention,
  checkLineTotal,
  lineDiscrepancy,
  reconcileInvoiceLines,
  auditAddenda,
  roundQty,
  packMathTotalUnits,
  impliedPackCount,
  detectPricePerItem,
  // Named in the cell's own sentence, so what the grid says about a line is
  // decided from the same unit `impliedPackCount` decided from.
  linePackUnit,
  missingStockCells,
  MONEY_REQUIRED_KEYS,
  REQUIRED_LINE_CELLS,
  isMissingCell,
} from "./invoiceLineMath";
import type { LineMath, Convention, AddendaSuspect } from "./invoiceLineMath";

// Re-exported so every existing importer of this module keeps working.
export {
  tolerance,
  num,
  money,
  round2,
  isEmptyValue,
  computeLineMath,
  matchesAny,
  invoiceConvention,
  checkLineTotal,
  lineDiscrepancy,
  reconcileInvoiceLines,
  auditAddenda,
  roundQty,
  packMathTotalUnits,
  detectPricePerItem,
};
export type { LineMath, Convention, AddendaSuspect };

/**
 * `confirmed` is an amber cell a person has since checked against the paper
 * and vouched for. It renders in the same blue as `manual` on purpose: to a
 * reviewer both mean "a person has dealt with this cell", which is the only
 * distinction the colour needs to carry. Which of the two it is, and why the
 * cell was flagged in the first place, is on hover.
 */
export type FlagLevel = "warn" | "error" | "manual" | "confirmed";

export type CellFlag = {
  level: FlagLevel;
  /** Shown in the cell's tooltip. Written for ops, not for developers. */
  reason: string;
  /**
   * Another cell on the same line that carries the SAME question (expiry date /
   * "No date"). Both stay painted; `countFlags` counts the pair once.
   */
  sameQuestionAs?: string;
};

/** Keyed by the line-item field name, matching the grid's column `key`. */
export type LineItemFlags = Record<string, CellFlag>;

/**
 * `applySplitToLineItems` writes "" (not null) when it cannot parse a pack
 * pattern, so emptiness has to be checked as well as absence. A real 0 is a
 * value, not a gap.
 */
const isMissing = isMissingCell;

/** First flag wins: an earlier, more specific reason is never overwritten. */
function put(flags: LineItemFlags, key: string, flag: CellFlag): void {
  if (!flags[key]) flags[key] = flag;
}

/** The server's unknown-code stamp (VI-10), while nobody has typed a name over it. */
function unknownCodeStillAsked(line: any): boolean {
  return line?.codeNotInMemory === true && String(line.itemName ?? "") === String(line.codeNotInMemoryName ?? "");
}

/**
 * Add what the MEMORY knows to a cell something else has already flagged,
 * keeping the level the first flag set.
 *
 * The stamps below are not a competing opinion about the cell. They are the
 * answer to it, and first-flag-wins was dropping them exactly when they were
 * worth most. Hadco 4455862: `splitUow` reads "C-24", which fails isKnownUnit,
 * so the unrecognised-unit check above claims the cell and `put` discarded
 * `splitUowProfileSuggested: "Case"`. The reviewer saw a pink "not a recognised
 * pack unit" and no hint that a person had already settled this item.
 *
 * The two conditions are correlated, not independent: the memory only holds an
 * answer for a pack unit BECAUSE somebody corrected an unreadable one on an
 * earlier invoice, so the error is nearly always sitting on the cell already.
 *
 * The level is never LOWERED. "C-24" is still not a value anyone entered,
 * several of these gate Confirm & sync, and ops works the "missing or
 * unreadable" count. The suggestion rides along in the tooltip instead.
 *
 * It IS raised, though (review 2026-08-24). Keeping the first flag's level
 * unconditionally meant an `AsPrinted` autocorrection, the one place on this
 * screen where a value contradicts the paper and pink by design, rendered
 * amber whenever a warn-level check reached the cell first. The severity a cell
 * shows is the highest anything has to say about it, in either order.
 */
const LEVEL_SEVERITY: Record<FlagLevel, number> = {
  // Blue is a statement about who filled the cell, not about how wrong it is,
  // so it never outranks a complaint.
  manual: 0,
  confirmed: 0,
  warn: 1,
  error: 2,
};

function putOrAppend(flags: LineItemFlags, key: string, flag: CellFlag): void {
  const existing = flags[key];
  if (!existing) {
    flags[key] = flag;
    return;
  }
  // Re-running over the same line must not stack the same sentence twice.
  if (existing.reason.includes(flag.reason)) return;
  const level =
    LEVEL_SEVERITY[flag.level] > LEVEL_SEVERITY[existing.level] ? flag.level : existing.level;
  flags[key] = { level, reason: `${existing.reason} ${flag.reason}` };
}

/**
 * A cell the grid shows but the extractor could not fill.
 *
 * `error` is reserved for fields something downstream actually consumes — a
 * blank there is a silent failure later (a line skipped at goods-received, a
 * price that never reaches the catalogue), not a cosmetic gap.
 *
 * The list itself is `REQUIRED_LINE_CELLS` in the mirrored invoiceLineMath twin
 * (QA USC-BUG-0061 / 0064): the server refuses a save, a Confirm & sync and a
 * catalogue row on exactly these cells, so the red cell here and the refusal
 * there are one list.
 */
const REQUIRED_CELLS = REQUIRED_LINE_CELLS;

/** The cells a catalogue row fills. MUST equal server `vendorItemProfileFields.CATALOGUE_FILLED_CELLS` (mirroredUtilParity). */
export const CATALOGUE_FILLED_CELLS: readonly string[] = ["itemName", "splitUow", "splitPackCount", "splitUnitSize", "splitItemUow", "splitItemUnits"];

/**
 * The columns the review grid marks with a star, derived from the list above
 * rather than typed out again beside the column definitions.
 *
 * A star and a red cell are the same statement made at two moments — "this has
 * to be filled in" before the fact, "this was not filled in" after it — and two
 * hand-maintained lists would eventually make them contradict each other: a
 * starred column that never turns red, or worse, a red cell on a column
 * nothing warned about.
 *
 * `error` only. The warn-level entries (Item Code) are worth having, but a star
 * on a field a reviewer may legitimately leave blank teaches people to ignore
 * stars.
 *
 * One requirement is deliberately absent from this set and cannot join it:
 * Units / Case, compulsory only on a line whose Pack Unit is a case. A star is
 * a statement about a COLUMN and that requirement is a property of a ROW, so
 * starring it would demand a pack count from the loose-goods lines beside it.
 * It turns red from its own check in `getLineItemFlags` and explains the
 * condition in the cell's reason. If a genuinely unconditional requirement is
 * ever added, it belongs in REQUIRED_CELLS above and the star follows on its
 * own.
 */
export const REQUIRED_LINE_CELL_KEYS: ReadonlySet<string> = new Set(
  REQUIRED_CELLS.filter((c) => c.level === "error").map((c) => c.key)
);

/**
 * D95: the starred columns of a COMPANY EXPENSE - it stocks nothing, so it owes only the
 * money cells (the same MONEY_REQUIRED_KEYS the server's confirm asks).
 */
export const MONEY_REQUIRED_CELL_KEYS: ReadonlySet<string> = new Set(
  Array.from(REQUIRED_LINE_CELL_KEYS).filter((k) => MONEY_REQUIRED_KEYS.includes(k))
);

/** The cells only a stocked line owes: every required cell that is not money, the pack count, the expiry. */
const STOCK_ONLY_CELL_KEYS: readonly string[] = [
  ...Array.from(REQUIRED_LINE_CELL_KEYS).filter((k) => !MONEY_REQUIRED_KEYS.includes(k)),
  "splitPackCount",
  "expiryDate",
  "expiryNotPrinted",
];

/** Values that make no sense whatever the invoice says. */
const NON_NEGATIVE_CELLS: { key: string; label: string }[] = [
  { key: "splitQuantity", label: "Quantity" },
  // Deliberately NOT in REQUIRED_CELLS below, which is the list of cells every
  // line owes whatever it is carrying. Plenty of lines are sold loose — by the
  // pound, by the gallon — and have no pack count to state. A line whose Pack
  // Unit says CASE is the exception, and it is checked separately against
  // `packCountRequired` because the requirement is a property of the ROW, not
  // of the column. Empty is a legitimate answer here for everything else;
  // negative is not, for anything.
  { key: "splitPackCount", label: "Units / Case" },
  { key: "splitItemUnits", label: "Total Units" },
  { key: "unitPrice", label: "Unit Price" },
  { key: "taxRate", label: "Tax Rate" },
  { key: "taxAmount", label: "Tax Amount" },
  { key: "lineTotal", label: "Line Total" },
];


/**
 * The money cells whose values have to agree with each other, that a person can
 * change without the line following.
 *
 * Not the quantity (D44 #19, VR-5): a person's quantity edit before stock-in
 * makes the server recalculate the line's total and tax, and after stock-in the
 * quantity cannot be edited at all. "This line adds up now, but not with that
 * change" was false for every quantity correction the owner decided to allow.
 *
 * The unit price only once the invoice is in stock (D72, 2026-09-26): before
 * stock-in the server recalculates the line from a person's price correction
 * too; after it the price stays editable and the line does NOT follow.
 */
const ARITHMETIC_FIELDS = ["unitPrice", "discount", "lineTotal", "taxRate", "taxAmount"];
const RECALCULATED_BEFORE_STOCK_IN = "unitPrice";

/**
 * What the vendor printed on a line a person corrected the quantity of —
 * `quantityAsPrinted`, `lineTotalAsPrinted`, `taxAmountAsPrinted`, written by the
 * server's `vendorLineCorrection` (D44 #55). The same `AsPrinted` spelling as
 * the item memory's autocorrections, but not one of them.
 */
const QUANTITY_CORRECTION_PRINTED_FIELDS: ReadonlySet<string> = new Set(["quantity", "lineTotal", "taxAmount"]);

/**
 * Would this edit break a line that currently adds up?
 *
 * Returns the sentence to put in front of the user, or null to save silently.
 * The flags alone were not enough here: an implausible figure (a $10 item
 * priced at 10000) goes in, the cell turns red, and if nobody scrolls back the
 * invoice carries it. Asking at the moment of the edit puts the arithmetic in
 * front of the person who can still remember what the document said. A quantity
 * is no longer asked about: see ARITHMETIC_FIELDS.
 *
 * A line that is ALREADY broken asks nothing — the user is most likely part way
 * through fixing it, and being challenged on every keystroke of a repair is how
 * a warning gets trained out of usefulness.
 *
 * `inStock: false` (the invoice has not been through Confirm & sync) skips the
 * unit price, which the server then recalculates the line from. Not stated,
 * the price is asked about, as it is after stock-in. So is the price of a line
 * worked out from its total (`unitPriceFromLineTotal`): exempt from D72, its
 * printed total stands and the typed price is judged against it. On such a
 * line it is the Line Total that is not asked about before stock-in: the
 * server works the price out again from it.
 */
export function describeLineTotalConflict(
  row: any,
  key: string,
  value: any,
  { inStock }: { inStock?: boolean } = {}
): string | null {
  if (!ARITHMETIC_FIELDS.includes(key)) return null;
  if (key === RECALCULATED_BEFORE_STOCK_IN && inStock === false && row?.unitPriceFromLineTotal !== true) return null;
  if (key === "lineTotal" && inStock === false && row?.unitPriceFromLineTotal === true) return null;

  const before = checkLineTotal(computeLineMath(row));
  if (before && !before.ok) return null;

  const after = checkLineTotal(computeLineMath({ ...row, [key]: value }));
  if (!after || after.ok) return null;
  return after.message;
}

/**
 * What the SERVER refused this line for, on the last Confirm & sync.
 *
 * The gates in `confirmVendorInvoiceInventory` know things this file cannot —
 * whether a twin is already stocked, whether the pack arithmetic holds against
 * `buildUom`, whether the unit survives `resolveRowUnit` — and they name the
 * exact cells. Carrying that verdict in here rather than painting it separately
 * keeps ONE answer to "what is wrong with this cell": the grid, the tooltip and
 * the refusal cannot disagree, because they are the same object.
 *
 * Cleared by the caller as soon as the invoice is saved or reloaded: it
 * describes the figures that were on screen when the confirm was refused.
 */
export type GateIssue = { cells: string[]; reason: string };

export function getLineItemFlags(
  line: any,
  validation?: any,
  ctx?: { convention?: Convention; gateIssue?: GateIssue | null; index?: number; stocks?: boolean }
): LineItemFlags {
  const flags: LineItemFlags = {};
  if (!line || typeof line !== "object") return flags;

  const m = computeLineMath(line);

  // FIRST, so the server's own reason is the one the reviewer reads. Everything
  // below is this file's reading of the line; this is the reading that actually
  // stopped the stock, and `put` keeps it — first flag wins.
  for (const key of ctx?.gateIssue?.cells || []) {
    if (typeof key === "string" && key) {
      put(flags, key, { level: "error", reason: ctx!.gateIssue!.reason });
    }
  }

  // --- Units we do not recognise ------------------------------------------
  // First, because "wrong" is a more useful thing to say about a cell than
  // "does not add up", and this is what catches the OCR "14OZ" -> "140Z"
  // corruption: the parser stores the leftover "Z" as if it were a unit.
  if (!isMissing(line.splitUow) && !isKnownUnit(line.splitUow)) {
    // MC-4: say what the notation DOES state, from the vocabulary's one reader,
    // because the pack unit is what every price conversion hangs off.
    const read = readUnitNotation(line.splitUow);
    // A different count already in Units / Case (from the description's pack
    // pattern, or typed) is a disagreement to check, not a figure to overwrite.
    const perCase = num(line.splitPackCount);
    const hint =
      read?.unit && read.packCount && perCase !== null && perCase > 0 && perCase !== read.packCount
        ? ` It states ${read.unit} with ${read.packCount} in it, but Units / Case is ${perCase}: check the invoice, then enter ${read.unit} here and the right count in Units / Case.`
        : read?.unit && read.packCount
        ? ` It reads as ${read.unit} with ${read.packCount} in it: enter ${read.unit} here and ${read.packCount} in Units / Case.`
        : read?.packCount
          ? ` It states a count (${read.packCount}), not a unit: choose the Pack Unit and enter ${read.packCount} in Units / Case.`
          : "";
    put(flags, "splitUow", {
      level: "error",
      reason: `"${line.splitUow}" is not a recognised pack unit — likely misread from the invoice.${hint}`,
    });
  }
  if (!isMissing(line.splitItemUow) && !isKnownUnit(line.splitItemUow)) {
    put(flags, "splitItemUow", {
      level: "error",
      reason: `"${line.splitItemUow}" is not a recognised unit — likely misread from the invoice.`,
    });
  }
  const sizeUnit = unitFromSizeText(line.splitUnitSize);
  if (sizeUnit && !isKnownUnit(sizeUnit)) {
    put(flags, "splitUnitSize", {
      level: "error",
      reason: `"${sizeUnit}" is not a recognised unit — check the size against the invoice (e.g. 140 Z should read 14 OZ).`,
    });
  }
  // THE TWO SLOTS HOLD EACH OTHER'S VALUES (ClickUp 86d47b59f).
  //
  // Both cells are recognised units, so every check above passes and the row
  // stores in silence — which is how the beef row came to carry a BOX as its
  // unit size and produce no conversion at all. `slotTypeIssues` is the rule
  // (utils/unitVocabulary, mirrored), and it only fires on positive evidence:
  // a DIMENSION in the pack slot, a CONTAINER in the size slot. An unclassified
  // unit — BAG, DOZ, SET — is left alone in both, so this cannot flag a legal
  // row as mistyped.
  //
  // WARN, not error. The owner has not said whether a mistyped slot should
  // block Confirm & sync, and several error-level flags here gate it; turning
  // one on without being asked would stop invoices that store correctly today.
  // Raising it later is one word.
  const slots = slotTypeIssues({ packUnit: line.splitUow, unitSize: line.splitUnitSize });
  if (slots.packUnit) {
    put(flags, "splitUow", {
      level: "warn",
      reason:
        `Pack Unit "${line.splitUow}" is a measure, not a container. This column is what the ` +
        `goods come PACKED IN (CASE, BOX, CARTON); how much is in one goes in Unit Size.`,
    });
  }
  if (slots.unitSize) {
    put(flags, "splitUnitSize", {
      level: "warn",
      reason:
        `Unit Size "${line.splitUnitSize}" is a container, not a size. This column is how much ` +
        `is in ONE of them — "14 OZ", "1 KG", "360 PCE" — and a container here leaves the row ` +
        `unable to convert at all.`,
    });
  }

  // A size with no number is half-read: "ML" alone says nothing about how much.
  if (!isMissing(line.splitUnitSize) && !/\d/.test(String(line.splitUnitSize))) {
    put(flags, "splitUnitSize", {
      level: "error",
      reason: `Unit Size "${line.splitUnitSize}" has no number — enter it as e.g. "500 ML".`,
    });
  }

  // --- Cells the extractor left empty --------------------------------------
  for (const cell of REQUIRED_CELLS) {
    if (isMissing(line[cell.key])) put(flags, cell.key, { level: cell.level, reason: cell.reason });
  }

  // --- A printed code the item memory does not know (VI-10) ----------------
  // The server stamps `codeNotInMemory` on a line whose code is all the paper
  // says (vendorItemProfile.service markUnknownCodes). Shown only while the
  // Item Name still holds what it held when stamped: once a reviewer types a
  // name the line is named, and that edit is what the memory learns. Warn: it
  // never blocks a Save or a Confirm.
  if (unknownCodeStillAsked(line)) {
    put(flags, "itemName", {
      level: "warn",
      reason:
        `Code ${line.itemCode || ""} is not in this supplier's item memory, and the invoice prints ` +
        `nothing else about the product. Type the item name and packaging once: the next invoice ` +
        `with this code fills them in.`,
    });
  }

  // --- The item memory could not be asked at all (USC-BUG-0040 / 0042 / 0041c) ---
  // `extracted.validation.itemMemorySkipped` is the server's stamp
  // (vendorInvoice.service.js loadProfileReplay, vendorItemProfile.service.js
  // recordManualEdit) for a reviewer naming a product on an invoice whose
  // supplier is only `suggested`: `resolveInvoiceVendorId` refuses to guess
  // across vendors, so nothing is recalled and nothing is learned, and with no
  // stamp at all the reviewer saw a blank row and read it as "the AI didn't
  // learn". Gated on the line actually holding a name — an empty line has no
  // identity for the memory to have looked up in the first place.
  //
  // AFTER the unknown-code check above and using `put` (first flag wins), on
  // purpose: a line with no name at all owes that more specific question
  // first, and typing a name over the printed code is what raises this one
  // instead — the two are never both live for the same cell.
  if (validation?.itemMemorySkipped === true && !isMissing(line.itemName)) {
    put(flags, "itemName", {
      level: "warn",
      reason: "Confirm or add the supplier to fill these cells from past invoices.",
    });
  }

  // --- The requirements that depend on the line (D65) -----------------------
  // Units / Case is compulsory when the Pack Unit says the goods were bought
  // by the pack — CASE, BOX, CARTON, PACK, BUNDLE, TRAY, BAG, SACK — and
  // optional otherwise (2026-08-21 call, widened 2026-09-25; the rule itself
  // is `packCountRequired`/`missingStockCells` in the mirrored twin, where the
  // server-side confirm gate reaches the same answer). Chicken leg quarters
  // bought by the case state how many the case holds, even when that is 1;
  // rice vinegar bought by the piece has no pack size to state.
  //
  // Read from `missingStockCells`, THE OWNER, rather than re-implemented here:
  // this used to be its own `packCountRequired(line) && isMissing(...)` check,
  // a second copy of exactly the blank-cell half of that rule with none of the
  // three D65 rules that followed it (a locked unit holding more than one, a
  // pack of one with no proof, a size typed into the count). One call now
  // carries all four, so the red cell and the Confirm & sync refusal it
  // matches can never again name a different reason for the same line.
  //
  // It gets NO star, and that is not an oversight. The star is drawn per
  // COLUMN, in the table header, and this requirement is per ROW — a star on
  // the column would tell a reviewer working a loose-goods invoice that every
  // line owes a pack count, which is the false demand REQUIRED_LINE_CELL_KEYS
  // exists to avoid. The red cell carries the condition in its own reason
  // instead, so it explains itself the moment it appears.
  // A NEGATIVE Units / Case is left to NON_NEGATIVE_CELLS below, same as it
  // always was: "cannot be negative" is a clearer thing to tell a reviewer
  // than "how many items one case holds has to be stated", and put()'s
  // first-flag-wins would otherwise let this block's message win by running
  // first. `missingStockCells` itself does not carry this narrowing — a
  // negative count still refuses Confirm & sync with its own reason — this is
  // only which of two true statements the CELL shows.
  const negativePackCount = num(line.splitPackCount) !== null && (num(line.splitPackCount) as number) < 0;
  if (!negativePackCount) {
    for (const cell of missingStockCells(line)) {
      if (cell.key !== "splitPackCount") continue; // splitItemUnits has its own, richer check below.
      put(flags, "splitPackCount", { level: "error", reason: cell.reason });
    }
  }

  // --- Values that cannot be right at all ----------------------------------
  for (const { key, label } of NON_NEGATIVE_CELLS) {
    const v = num(line[key]);
    if (v !== null && v < 0) {
      put(flags, key, { level: "error", reason: `${label} cannot be negative.` });
    }
  }
  if (m.taxRate !== null && m.taxRate > 100) {
    put(flags, "taxRate", {
      level: "error",
      reason: `A tax rate of ${m.taxRate}% is not a real rate — it is likely the tax AMOUNT read into the rate column.`,
    });
  }
  // base is already net of the discount, so a negative base means the discount
  // exceeded the goods.
  //
  // Reverted to reading `m.base` directly (D82 pre-push review, item 3): the
  // `bases[0]` workaround was needed while `base`'s own search tried percent
  // and gross too, so a line whose print happened to match one of THOSE
  // readings could clear this guard while its whole-line reading stayed
  // negative. `base`'s search is narrowed to whole-line and per-unit only now
  // (see computeLineMath's own comment), and per-unit can never be negative on
  // a positive-gross line — `discountReadings` refuses any per-unit reading
  // that would flip the line's own sign. So `m.base` is once again either the
  // whole-line reading itself, or a per-unit reading that is provably NOT this
  // guard's business (it reconciles, at a non-negative amount) — `m.base`
  // suffices on its own again.
  if (m.discount > 0 && m.base !== null && m.base < 0) {
    put(flags, "discount", {
      level: "error",
      reason:
        `A discount of ${money(m.discount)} is larger than the line itself ` +
        `(${m.qty} x ${money(m.unitPrice as number)}).`,
    });
  }

  // --- Packs vs individual units -------------------------------------------
  // Total Units is what actually reaches inventory. It is packs x units-per-pack,
  // so it can never be fewer than the number of packs.
  //
  // It CAN be fractional. Meat, produce and cheese are billed by weight, and
  // 10.6 KG is the figure the invoice states — the arithmetic used to round it
  // to 11 and this check rounded the same way, so the fabricated unit passed
  // unremarked. What remains unanswered is whether a fraction is plausible for
  // THESE goods: 10.6 kg is a delivery, 10.6 tins is a misread, and nothing on
  // the line yet says which of the two it is holding. The divides-unevenly
  // warning below is the closest thing to that question and it is still asked
  // of both.
  if (m.qty !== null && m.qty > 0) {
    const units = num(line.splitItemUnits);
    const perCase = num(line.splitPackCount);

    if (units !== null && units > 0) {
      if (units < m.qty) {
        put(flags, "splitItemUnits", {
          level: "error",
          reason:
            `Total Units (${units}) is fewer than the Quantity (${m.qty}). Total Units is the ` +
            `number of individual items across all ${m.qty} packs, so it cannot be smaller.`,
        });
      } else if (perCase !== null && perCase > 0) {
        // With the pack count on the line the contradiction can be named
        // outright instead of inferred from a division that does not come out
        // whole. Both cells are flagged: the figures disagree, and which of
        // them is wrong is exactly what the reviewer has to decide.
        // The owner's arithmetic. This check used to round the product to a
        // whole number exactly as the two writers did, so on a weight line it
        // agreed with the fabricated figure and said nothing: 10.6 x 1 rounded
        // to 11 on the way in, and to 11 again here.
        const expected = packMathTotalUnits(m.qty, perCase);
        if (expected !== null && roundQty(units) !== expected) {
          const reason =
            `${m.qty} x ${perCase} per case = ${expected}, but Total Units says ${units}. ` +
            `Total Units is what goes into stock, so correct whichever of the three is wrong.`;
          put(flags, "splitItemUnits", { level: "error", reason });
          put(flags, "splitPackCount", { level: "error", reason });
        }
      } else if (!Number.isInteger(units / m.qty)) {
        put(flags, "splitItemUnits", {
          level: "warn",
          reason:
            `Total Units (${units}) is not a whole number of items per pack ` +
            `(${units} / ${m.qty} = ${(units / m.qty).toFixed(2)}). Check the pack size.`,
        });
      }
    }

    // A pack count recovered by dividing Total Units, then invalidated by
    // someone editing that total. It is no longer evidence of anything, and
    // leaving it unmarked lets a stale figure read as a reading off the paper.
    if (line.splitPackCountSource === "stale" && perCase !== null) {
      put(flags, "splitPackCount", {
        level: "warn",
        reason:
          `Total Units was entered by hand after this pack count was read, so the two no ` +
          `longer come from the same source. Confirm ${perCase} items per case still holds.`,
      });
    }
  }

  // --- A Total Units the UNIT WORD supplied ---------------------------------
  // Goods sold loose print no pack pattern, so this column used to arrive empty
  // and red on every line of a produce invoice — 38 of them on D&M Vegetables
  // 28815 — with a reason that was wrong about the cost ("this line will be
  // skipped when stock is received"; `buildStock` falls back to the quantity).
  // `impliedPackCount` fills it now: a pound holds one pound.
  //
  // Marked rather than slipped in, the same as the OCR repair and the per-item
  // price rebase above. Nothing on the paper states this figure, and a reviewer
  // who sees LBS where the supplier meant a case has to be able to tell that
  // the number beside it is ours. Blue, not amber: it needs reading, not
  // correcting, and `missingStockCells` does not gate on it either way.
  if (line.splitItemUnitsFromUnitWord === true) {
    const unit = linePackUnit(line) || "this unit";
    putOrAppend(flags, "splitItemUnits", {
      level: "confirmed",
      reason:
        `Taken as the Quantity because the line is billed by the ${unit}, which holds one ` +
        `of itself — the invoice states no pack size. Not a figure read off this invoice; ` +
        `enter Units / Case if it was really sold by the pack.`,
    });
  }

  // --- A Units / Case the UNIT NOTATION supplied (MC-4) ----------------------
  // "CS10PK" in the invoice's unit column, read as a case of 10. Blue for the
  // same reason as above: the figure is ours, and a reviewer has to be able to
  // tell it from one printed in the description.
  if (line.splitPackCountFromUnitWord === true) {
    putOrAppend(flags, "splitPackCount", {
      level: "confirmed",
      reason:
        "Read from the pack notation in the invoice's unit column (e.g. CS10PK = a case of 10). " +
        "Check it against the invoice; the item memory learns it only once a person confirms or types it.",
    });
  }

  // --- A Unit Size the PACK UNIT supplied -----------------------------------
  // The other half of D18, and the one that moves money rather than a colour:
  // `weightTierFromSize` reads this cell into `weightPerLevel1`, which
  // `costEngine.computeCostLevels` divides for ttPerKg. So the reviewer is told
  // plainly what the per-kg figure now rests on. It rests on something true —
  // one pound weighs one pound — but the sentence has to be checkable, because
  // the reading is only as good as the Pack Unit it was taken from.
  if (line.splitUnitSizeFromUnitWord === true) {
    const unit = linePackUnit(line) || "this unit";
    putOrAppend(flags, "splitUnitSize", {
      level: "confirmed",
      reason:
        `Taken as one ${unit} because that is how the line is billed — the invoice states no ` +
        `pack size. This is what the per-kg cost is worked out from, so correct it if the ` +
        `goods really came in a pack.`,
    });
  }

  // --- Units we corrected ourselves ----------------------------------------
  // The parser undoes the known OCR misread ("140Z" -> "14OZ"), but a repair is
  // still a guess about what the paper said. Amber, not silent: ops confirms it.
  // Never overwrites a red flag — a cell that is still wrong matters more than
  // one we already fixed.
  if (line.unitRepairedFromOcr === true) {
    const repairedNote =
      "Unit was repaired from OCR (the scan read OZ as 0Z). Confirm it against the invoice.";
    put(flags, "splitUnitSize", { level: "warn", reason: repairedNote });
    put(flags, "splitItemUow", { level: "warn", reason: repairedNote });
  }

  // A price the supplier printed PER ITEM, restated as the price of a case so
  // the line reconciles and the catalogue costs it correctly. Amber for the
  // same reason the OCR repair is: the figure on screen no longer matches the
  // figure on the paper, and a reviewer checking one against the other must be
  // able to see why. The printed value is kept on the line.
  if (line.unitPriceRebasedPerPack === true) {
    const printed = num(line.unitPriceAsPrinted);
    const factor = num(line.unitPriceRebaseFactor);
    put(flags, "unitPrice", {
      level: "warn",
      reason:
        printed !== null && factor !== null
          ? `Invoice prints ${money(printed)} per item. Restated as ${money(
              num(line.unitPrice) ?? 0
            )} per case (${factor} per case).`
          : "Restated from a per-item price to a case price.",
    });
  }

  // A price the supplier did NOT print, worked out by the processor from the
  // line total (Lawrence Marketing, owner 2026-09-26: "auto-fill + flag").
  // Amber and non-blocking, like the rebase above: the figure is ours, not the
  // paper's. The server clears the stamp on the reviewer's first price edit.
  // The base is the one the server's `impliedUnitPrice` divided, stored beside
  // the stamp — on a VAT-inclusive invoice the total less its VAT — so the note
  // prints the arithmetic actually used rather than a second derivation of it.
  // The price is carried to 4dp, so it is printed as stored when it has more
  // than two.
  if (line.unitPriceFromLineTotal === true && m.unitPrice !== null && m.lineTotal !== null) {
    const base = num(line.unitPriceFromLineTotalBase) ?? m.lineTotal;
    const divided =
      base !== m.lineTotal ? `${money(m.lineTotal)} incl. VAT → ${money(base)}` : money(base);
    const price = round2(m.unitPrice) === m.unitPrice ? money(m.unitPrice) : String(m.unitPrice);
    put(flags, "unitPrice", {
      level: "warn",
      reason: `Unit price worked out from the line total (${divided} ÷ ${m.qty} = ${price}) — check it.`,
    });
  }

  // --- What the vendor item memory filled in, replaced, or questioned -------
  //
  // Deliberately AFTER the required-cell and pack-math checks above: `put` is
  // first-flag-wins, and a cell that is still wrong matters more than one we
  // have an opinion about. Same reasoning as the OCR-repair block.
  //
  // Read off the stamps rather than a shared field list, so the server can
  // widen what it remembers without a matching edit here — and so no new
  // mirrored pair is created for a rule that is only ever asked on one side.
  //
  // THREE LEVELS, and the difference is what the reviewer has to do:
  //   FromProfile      the cell was EMPTY and history filled it. Blue: this
  //                    would otherwise be a red gap, and a person did settle it
  //                    — on another invoice. Worth a glance, not a decision.
  //   AsParsed         we replaced our OWN parser's reading. Amber: the value
  //                    on screen no longer matches what this document's text
  //                    parses to, exactly like the OCR repair and the per-item
  //                    price rebase, and that must be checkable against paper.
  //   ProfileSuggested the invoice states something else. Amber: nothing was
  //                    written, and only the reviewer can settle it.
  {
    const seen = num(line.profileMatch?.invoicesSeen) ?? 0;
    const supplier = String(line.profileMatch?.vendorName || "").trim();
    const history =
      (seen > 0 ? `${seen} past invoice${seen === 1 ? "" : "s"}` : "past invoices") +
      (supplier ? ` from ${supplier}` : "");

    // A stamp is only meaningful beside the cell it describes. Without this
    // guard a stamp left behind on a line whose base cell is empty renders
    // `showing "undefined"` at the reviewer, which reads as a bug in the
    // figures rather than in the annotation.
    const described = (field: string) => !isMissing(line[field]);

    for (const key of Object.keys(line)) {
      if (key.endsWith("FromProfile") && line[key] === true) {
        const field = key.slice(0, -"FromProfile".length);
        if (!described(field)) continue;
        putOrAppend(flags, field, {
          level: "confirmed",
          reason:
            `This invoice left the cell blank. Filled in as "${line[field]}" from ${history} ` +
            `for the same item. Correct it if this delivery is different.`,
        });
      } else if (key.endsWith("AsParsed") && !isMissing(line[key])) {
        const field = key.slice(0, -"AsParsed".length);
        if (!described(field)) continue;
        putOrAppend(flags, field, {
          level: "warn",
          reason:
            `Read from this invoice as "${line[key]}"; showing "${line[field]}" instead, ` +
            `settled over ${history}. The description column is unchanged — check it.`,
        });
      } else if (key.endsWith("AsPrinted") && !isMissing(line[key])) {
        // The memory CORRECTED a cell the supplier printed, because what was
        // printed is not a value for that field ("C-24" as a pack unit). Pink,
        // not amber: this is the one place a value on screen contradicts the
        // paper, so it must be the loudest thing in the row, and the tooltip
        // has to say what was printed as well as what replaced it.
        //
        // unitPriceAsPrinted is handled separately (lines 462-474) by the
        // per-item price rebase logic and is not a vendor-memory correction.
        const field = key.slice(0, -"AsPrinted".length);
        if (field === "unitPrice") continue;
        // Nor are the figures a PERSON's quantity correction keeps (D44 #55,
        // VR-5): the memory never touches a transactional cell, and painting
        // the recalculated total pink as "corrected from past invoices" is
        // false. The page shows them as "Vendor printed" instead.
        if (QUANTITY_CORRECTION_PRINTED_FIELDS.has(field)) continue;
        if (!described(field)) continue;
        putOrAppend(flags, field, {
          level: "error",
          reason:
            `Corrected to "${line[field]}" from ${history} for the same item. ` +
            `This invoice printed "${line[key]}", which is not a usable value here. ` +
            `Change it if this delivery is different, and the memory will learn what you enter.`,
        });
      } else if (key.endsWith("ProfileSuggested") && !isMissing(line[key])) {
        const field = key.slice(0, -"ProfileSuggested".length);
        if (!described(field)) continue;
        putOrAppend(flags, field, {
          level: "warn",
          reason:
            `This invoice says "${line[field]}", but ${history} settled this item as ` +
            `"${line[key]}". Nothing has been changed — enter ${line[key]} to apply it.`,
        });
      } else if (key.endsWith("FromCatalogueRow") && line[key] === true) {
        // D177: a blank cell took the same-name row. Amber: nobody has checked it against this
        // delivery, and Confirm counts it checked. Total Units carries the same stamp.
        const field = key.slice(0, -"FromCatalogueRow".length);
        if (!described(field)) continue;
        putOrAppend(flags, field, {
          level: "warn",
          reason:
            `This invoice left the cell blank. Filled in as "${line[field]}" from the Master Catalogue ` +
            `row with the same supplier and item name. Correct it if this delivery is different.`,
        });
      }
    }

    // Total Units is not remembered — it is quantity x pack count, which is
    // this delivery. It is recomputed when the memory changes the pack count,
    // and saying so is the difference between a figure derived from the number
    // beside it and one read off the paper.
    if (line.splitItemUnitsFromProfileMath === true) {
      putOrAppend(flags, "splitItemUnits", {
        level: "confirmed",
        reason:
          `Recalculated as ${line.splitQuantity} x ${line.splitPackCount} after the pack size ` +
          `was filled in from ${history}. Not a figure read off this invoice.`,
      });
    }
  }

  // --- Expiry ---------------------------------------------------------------
  // Confirm & sync refuses a line that will stock something in while it has
  // neither a date nor the "no date" tick. Flagged here so the reviewer sees it
  // in the row, rather than discovering it as a refusal after pressing the
  // button — the server gate is the authority, this is the same question asked
  // early. Warn, not error: it does not stop a Save, only a Confirm.
  //
  // Only lines that will actually stock something in are asked, matching the
  // server's buildStock test — a zero-quantity line opens no batch to date.
  {
    const stocking = (num(line.splitQuantity) ?? 0) > 0 || (num(line.splitItemUnits) ?? 0) > 0;
    const answered =
      !isMissing(line.expiryDate) ||
      !isMissing(line.expiryDateParsed) ||
      line.expiryNotPrinted === true;

    if (stocking && !answered) {
      const note =
        "No expiry date for this line. Enter the date off the packaging, or tick “No date” " +
        "if the goods carry none — Confirm & sync will not run until one or the other is done.";
      put(flags, "expiryDate", { level: "warn", reason: note });
      put(flags, "expiryNotPrinted", { level: "warn", reason: note, sameQuestionAs: "expiryDate" });
    }
  }

  // --- VAT rate provenance -------------------------------------------------
  // The backend has always recorded whether it READ a rate or fell back to one;
  // until now nothing showed it per line.
  if (line.taxRateUnresolved === true) {
    // Two sources can answer, and which one did is worth saying: the item
    // memory is keyed on (this supplier, this item) and holds a rate a reviewer
    // settled, where the catalogue is keyed on item name across every supplier.
    const suggested = line.taxRateSuggested;
    const sourceNote =
      suggested == null
        ? null
        : line.taxRateSuggestedSource === "vendorItemProfile"
        ? "was confirmed on a past invoice from this supplier"
        : line.taxRateSuggestedSource === "masterCatalogue"
        ? "is recorded in the Master Catalogue"
        : null;
    put(flags, "taxRate", {
      level: "warn",
      reason: sourceNote
        ? `No VAT rate on this invoice, but ${suggested}% ${sourceNote} for this item. Enter ${suggested} to apply it — the amount will be calculated.`
        : "No VAT rate could be read for this line. Enter the rate and the amount will be calculated.",
    });
  } else if (line.taxRateDefaulted === true) {
    put(flags, "taxRate", {
      level: "warn",
      reason: "VAT rate was defaulted, not read from the invoice. Confirm it against the document.",
    });
  } else if (line.taxRateSuggested != null) {
    put(flags, "taxRate", {
      level: "warn",
      reason: `VAT rate is a suggestion (${line.taxRateSuggested}%) rather than a printed value. Confirm it.`,
    });
  } else if (line.taxRateInferred === true) {
    // Not printed anywhere on the document: worked out from the invoice's own
    // tax total, because exactly one combination of lines reproduces it. Exact
    // arithmetic, but still an inference about which goods were taxed.
    put(flags, "taxRate", {
      level: "warn",
      reason:
        `This invoice prints no VAT column. ${line.taxRate}% here is the only reading of the ` +
        `lines that reproduces the tax total at the foot of the invoice. Confirm it.`,
    });
  } else if (isMissing(line.taxRate) && !(num(line.taxAmount) ?? 0)) {
    // Only a gap while the line charges no tax. A line that DOES charge tax
    // without stating a rate is a contradiction, and the block below says so in
    // red rather than letting this amber note claim the cell first.
    put(flags, "taxRate", {
      level: "warn",
      reason: "Tax Rate is empty. Enter it (0 for a zero-rated line) and the amount will be calculated.",
    });
  }

  // --- VAT rate vs VAT amount ----------------------------------------------
  // "The rate is filled in but the amount is not" was the single most common
  // state on the review screen: one of the pair is arithmetic on the other, and
  // nothing was doing that arithmetic once a base existed.
  if (m.expectedTax !== null) {
    if (isMissing(line.taxAmount) && m.expectedTax === 0) {
      // A zero-rated line owes nothing; a blank there is not worth a colour.
    } else if (isMissing(line.taxAmount)) {
      put(flags, "taxAmount", {
        level: "error",
        reason:
          `Tax Amount is empty, but this line states ${m.taxRate}% on a base of ` +
          `${money(m.base as number)} — that is ${money(m.expectedTax)}.`,
      });
    } else if (
      m.taxAmount !== null &&
      // Against every reading of the discount, not just ours — see LineMath.bases.
      !matchesAny(m.taxAmount, m.bases.map((b) => Number(((b * (m.taxRate as number)) / 100).toFixed(2))))
    ) {
      put(flags, "taxAmount", {
        level: "error",
        reason:
          `${m.taxRate}% of ${money(m.base as number)} is ${money(m.expectedTax)}, ` +
          `but Tax Amount says ${money(m.taxAmount)}.`,
      });
    }
  } else if (m.taxAmount !== null && m.taxAmount > 0 && isMissing(line.taxRate)) {
    put(flags, "taxRate", {
      level: "error",
      reason: `This line charges ${money(m.taxAmount)} of tax but states no rate.`,
    });
  }

  // An amount we calculated because the invoice printed none. Where the figures
  // reproduce the invoice's own tax total ('reconciled') they are proven and
  // stay unmarked; where there was no total to check against, ops confirms.
  //
  // Whether the FOOT prints a tax total is not what the 'derived' stamp says (it
  // is written at extraction and only means "we calculated this"): it is the
  // server's verdict on `validation.totalsEvidence.taxTotal`, the one the Totals
  // card words under Tax Total ("Read from the document" / "Not found on the
  // document"). The note reads the same verdict, so it cannot call the foot empty
  // while the card says the total was read (lane 5, V1). It claims an absent
  // total only on that verdict; with none, or a read total, it says nothing of it.
  if (line.taxAmountSource === "derived") {
    const totalNotOnDocument = validation?.totalsEvidence?.taxTotal === "not_on_document";
    put(flags, "taxAmount", {
      level: "warn",
      reason:
        "This invoice prints no tax amount on the line" +
        (totalNotOnDocument ? ", and the tax total was not found on the document either" : "") +
        ", so the amount was calculated from the line's rate. Confirm it against the document.",
    });
  }

  // --- Does the line add up? ------------------------------------------------
  // Recomputed here rather than read off the server so it reflects what is on
  // screen. This is what makes an implausible hand-typed quantity visible: it
  // stops agreeing with the price and the printed total immediately.
  const totalCheck = checkLineTotal(m);
  if (totalCheck && !totalCheck.ok) {
    // Name the money. "Does not reconcile" is a puzzle; "the supplier has
    // charged you 20.00 more than their own quantity and price come to" is an
    // instruction. This is the reported failure: 10 packs at 10 billed as 120,
    // paid without anyone noticing.
    const gap = lineDiscrepancy(m, ctx?.convention);
    let reason = totalCheck.message;
    if (gap && gap.difference > 0) {
      reason = `Overcharged by ${money(gap.difference)}. ${totalCheck.message}`;
    } else if (gap && gap.difference < 0) {
      reason = `Undercharged by ${money(Math.abs(gap.difference))}. ${totalCheck.message}`;
    }

    // One shape of mismatch the arithmetic can name outright: the line fails
    // against its pack count and passes against its item count, which is a
    // supplier pricing per item against a quantity in cases. It outranks the
    // overcharge wording because it is not an overcharge at all — the invoice
    // is right and the COLUMN is being read wrong, and calling it an overcharge
    // sends the reviewer to argue with a supplier who has done nothing wrong.
    //
    // The Unit Price cell carries it alone. The other three cells keep the
    // general message: which of them to correct is the reviewer's call, and
    // pointing all four at the price would prejudge it.
    //
    // Put on the Unit Price cell FIRST and with its own string — `put` is
    // first-wins, so claiming the cell here is what keeps the general message
    // off it, and `reason` is deliberately left alone because the loop below
    // hands it to the other three.
    const perItem = detectPricePerItem(line);
    if (perItem) {
      put(flags, "unitPrice", { level: "error", reason: perItem.message });
    }

    // The server's own wording when it saw the same mismatch, so the grid and
    // the discrepancy panel do not describe one problem two ways.
    const lineFlags: any[] = Array.isArray(validation?.lineFlags) ? validation.lineFlags : [];
    const match = lineFlags.find(
      (f: any) => f?.lineNo != null && String(f.lineNo) === String(line.lineNo)
    );
    if (match?.issue) reason = `${reason} (${match.issue})`;

    for (const key of ["splitQuantity", "unitPrice", "discount", "lineTotal"]) {
      put(flags, key, { level: "error", reason });
    }
  }

  // --- Name and figures on different printed rows (VI-2) -------------------
  // The server found this line's total printed on a row naming another product
  // (src/utils/printedFigures.js lineAlignment, matched by POSITION, so the
  // caller passes `index`). Last, so anything firmer about the cell wins; amber
  // and confirmable, never a refusal (PROVISIONAL O-16). USC-BUG-0004.
  const alignment: any[] = Array.isArray(validation?.lineAlignmentFlags) ? validation.lineAlignmentFlags : [];
  if (ctx?.index !== undefined && alignment.some((f: any) => f?.index === ctx.index)) {
    put(flags, "itemName", {
      level: "warn",
      reason:
        "This line's price and total are printed beside a different product on the document. " +
        "Check the paper: a vessel name or heading read as a line pushes every name one row up.",
    });
  }

  // --- Filled from the catalogue row the printed code names -----------------
  // A code-identified supplier's line (PriceSmart) is read at extraction as the
  // Master Catalogue row its code names (masterCatalogueVendorSync
  // `applyCatalogueRowsToLines`; BUSINESS_RULES#catalogue-vendor-item-code). Blue,
  // the existing "confirmed" level: the system supplied these cells from a row a
  // person already settled, so they need a glance, not a decision. LAST, so `put`
  // paints only a cell no other rule flagged: an error always outranks a
  // provenance note (a Total Units typed against the row's pack is no longer
  // qty x pack, and that pink must show). A cell the row left blank is skipped.
  if (line.catalogueRowId) {
    const code = line.catalogueMatchedByCode || line.itemCode || "";
    // Total Units is the row's only while it is still qty x the row's pack, stated
    // or implied: a figure typed over an implied pack (a KG row) passes every check
    // above and would otherwise be labelled the catalogue's.
    const rowTotal = packMathTotalUnits(
      num(line.splitQuantity),
      num(line.splitPackCount) ?? impliedPackCount(line)
    );
    for (const cell of CATALOGUE_FILLED_CELLS) {
      if (isMissing(line[cell])) continue;
      if (cell === "splitItemUnits" && (rowTotal === null || num(line.splitItemUnits) !== rowTotal)) continue;
      put(flags, cell, {
        level: "confirmed",
        reason:
          `Filled from the catalogue row for item code ${code}. ` +
          "Correct it on the catalogue row if the product is not what the code says.",
      });
    }
  }

  // D95: a company expense stocks nothing, so it is not asked for the cells only stock needs.
  if (ctx?.stocks === false) for (const key of STOCK_ONLY_CELL_KEYS) delete flags[key];

  return flags;
}

/** Tailwind classes for a flagged cell. Mirrors the override highlights on ClientInvoicePage. */
export function flagCellClass(flag?: CellFlag): string {
  if (!flag) return "";
  if (flag.level === "error") return "bg-rose-50 ring-1 ring-inset ring-rose-300";
  if (flag.level === "manual" || flag.level === "confirmed")
    return "bg-blue-50 ring-1 ring-inset ring-blue-300";
  return "bg-amber-50 ring-1 ring-inset ring-amber-300";
}

/**
 * The worst of several flags. Used where one visible column stands for more
 * than one stored field — the Unit Size column edits both the size and its unit.
 *
 * error > warn > confirmed > manual: a cell that is still wrong says so even if
 * a person also touched it — correctness outranks provenance.
 */
export function worstFlag(...flags: (CellFlag | undefined)[]): CellFlag | undefined {
  const present = flags.filter(Boolean) as CellFlag[];
  if (!present.length) return undefined;
  return (
    present.find((f) => f.level === "error") ||
    present.find((f) => f.level === "warn") ||
    present.find((f) => f.level === "confirmed") ||
    present[0]
  );
}

/**
 * Count of flagged cells across every line — drives the review summary.
 *
 * `confirmed` holds the dotted paths a person has already vouched for. Those
 * come out of the warning count and are reported separately: leaving them in
 * would mean the legend went on saying "3 needs confirming" straight after
 * somebody confirmed all three, which is the complaint this whole path exists
 * to answer.
 */
export function countFlags(
  lineItems: any[],
  validation?: any,
  opts?: { confirmed?: Iterable<string>; basePath?: string; stocks?: boolean }
): { errors: number; warnings: number; confirmed: number } {
  const confirmedSet = new Set(opts?.confirmed || []);
  const basePath = opts?.basePath || "extracted.lineItems";
  let errors = 0;
  let warnings = 0;
  let confirmed = 0;
  const items = Array.isArray(lineItems) ? lineItems : [];
  items.forEach((li, index) => {
    const flags = getLineItemFlags(li, validation, { index, stocks: opts?.stocks });
    for (const [key, f] of Object.entries(flags)) {
      // Blue is already settled (auto-read or confirmed): nothing for a human to do.
      if (f.level === "manual" || f.level === "confirmed") continue;
      if (f.sameQuestionAs && flags[f.sameQuestionAs]) continue;
      if (f.level === "error") errors += 1;
      else if (confirmedSet.has(`${basePath}.${index}.${key}`)) confirmed += 1;
      else warnings += 1;
    }
  });
  return { errors, warnings, confirmed };
}

/** One amber cell, located well enough to be confirmed or written to. */
export type FlaggedCell = { index: number; key: string; path: string; reason: string };

/**
 * The amber cells on an invoice, split by whether confirming them would mean
 * anything.
 *
 * `confirmable` is a cell that HOLDS a value the extractor could not prove —
 * a VAT rate it defaulted to, an amount it calculated because the invoice
 * printed none. There is something on screen to check against the paper, so a
 * person saying "that is right" settles it.
 *
 * `blank` is a cell flagged precisely because it is empty: an expiry date
 * nobody has recorded, a rate nobody has entered. There is nothing to vouch
 * for, and letting these be confirmed would paint them blue while leaving
 * Confirm & sync refusing the invoice for a reason no longer visible anywhere.
 * They need a value typed in, and they keep saying so.
 *
 * Cells already confirmed are in neither list — they are no longer amber.
 */
export function collectFlaggedCells(
  lineItems: any[],
  validation?: any,
  opts?: { convention?: Convention; confirmed?: Iterable<string>; basePath?: string; stocks?: boolean }
): { confirmable: FlaggedCell[]; blank: FlaggedCell[] } {
  const confirmedSet = new Set(opts?.confirmed || []);
  const basePath = opts?.basePath || "extracted.lineItems";
  const confirmable: FlaggedCell[] = [];
  const blank: FlaggedCell[] = [];

  const items = Array.isArray(lineItems) ? lineItems : [];
  items.forEach((li, index) => {
    const flags = getLineItemFlags(li, validation, { convention: opts?.convention, index, stocks: opts?.stocks });
    for (const [key, flag] of Object.entries(flags)) {
      if (flag.level !== "warn") continue;
      const path = `${basePath}.${index}.${key}`;
      if (confirmedSet.has(path)) continue;
      const cell: FlaggedCell = { index, key, path, reason: flag.reason };
      // `false` is blank too. The one boolean column here is the expiry "No
      // date" tick, and an unticked box is an unanswered question, not a value
      // a reviewer could put their name to.
      // The unknown-code question (VI-10) sits on an Item Name that holds the
      // code itself: a value, but not an answer. Swept as checked, it taught
      // the memory the code as the name (review 2026-09-15).
      const value = li?.[key];
      const hasValue =
        !isEmptyValue(value) && value !== false && !(key === "itemName" && unknownCodeStillAsked(li));
      (hasValue ? confirmable : blank).push(cell);
    }
  });

  return { confirmable, blank };
}

// ---------------------------------------------------------------------------
// Invoice level
// ---------------------------------------------------------------------------

/**
 * What KIND of thing an issue is — which is not the same question as how severe
 * it is (`level`).
 *
 * Every issue used to be anonymous, and the review screen had one bucket for
 * all of them: the green "Adds up" note under the Totals card rendered only when
 * `issues` was completely empty. So an invoice whose arithmetic reconciled to
 * the cent lost its tick because its supplier's credit terms were never
 * configured, or because one line did not say how big its pack was. Reported
 * from the floor as the green message appearing on one invoice and not on the
 * next, with nothing on screen explaining the difference — and the answer was
 * that the tick was never about the arithmetic alone.
 *
 *   arithmetic — the invoice does not agree with its own figures. THE only kind
 *                that decides whether the invoice adds up.
 *   pricing    — the lines are billed above or below quantity x unit price.
 *                Money, not arithmetic: the totals can still reconcile.
 *   freight    — a shipping or discount figure that needs a person to look at
 *                the document.
 *   stock      — the line cannot be received into inventory as it stands.
 *   document   — the lines may not be what the paper prints: a line read beside
 *                another product's figures, a priced row no line came from, or
 *                a line with no name and no price (VI-2). Warn only (O-16).
 *
 * Anything that reaches `issues` at `error` level still blocks Confirm & sync,
 * whatever its kind. Separating the kinds changes what the screen SAYS, never
 * what the gate allows through.
 */
export type IssueKind = "arithmetic" | "pricing" | "freight" | "stock" | "document";

export type InvoiceIssue = {
  level: FlagLevel;
  kind: IssueKind;
  title: string;
  message: string;
};

/**
 * The issues that decide whether the invoice adds up, and nothing else.
 *
 * A named owner rather than a `.filter` at each call site: "does this invoice
 * reconcile" is asked by the green note, by the heading of the discrepancy
 * panel, and by the row badge, and those three drifting apart is how this bug
 * started.
 */
export function arithmeticIssues(issues: InvoiceIssue[]): InvoiceIssue[] {
  return (issues || []).filter((i) => i.kind === "arithmetic");
}

/** Everything the invoice's own arithmetic has no opinion about. */
export function nonArithmeticIssues(issues: InvoiceIssue[]): InvoiceIssue[] {
  return (issues || []).filter((i) => i.kind !== "arithmetic");
}

/**
 * The badge beside an invoice number on the vendor invoice list (D109.8, owner
 * decision 2026-10-08).
 *
 *   calc  — red. The arithmetic does not add up (`arithmeticIssues`). Nothing else.
 *   check — neutral grey. Something else is open: a pack size, pricing, freight,
 *           stock or a document question, or a pink line cell. Shown only when
 *           `calc` is not.
 *   null  — nothing, or a human has settled the invoice (Verify / Confirm & sync).
 *
 * Line cell errors are never `calc`: line flags carry no kind, and the line-level
 * "billed differently from quantity x unit price" finding is the `pricing` kind
 * above — money, not arithmetic. So `lineErrors` only ever raises `check`.
 */
export type RowBadge = "calc" | "check" | null;

export function rowBadge(issues: InvoiceIssue[], lineErrors: number, humanSettled: boolean): RowBadge {
  if (humanSettled) return null;
  if (arithmeticIssues(issues).length > 0) return "calc";
  if ((issues || []).length > 0 || lineErrors > 0) return "check";
  return null;
}

/**
 * What the note under the Totals card may claim, and in which colour.
 *
 *   no-grand-total — grey, never a tick. With no printed Grand Total nothing
 *                    can raise an arithmetic issue, so `reconciled` comes out
 *                    true by default: an absence of evidence, not a match.
 *                    Alstons INV0460969 was scanned with the foot of the page
 *                    cut off and the note still read "Adds up" — the client
 *                    asked what it was matching against (prod USC-BUG-0023).
 *   pink-remains   — amber. The lines do come to the Grand Total, but a cell
 *                    is still pink: a misread line tax can sit on totals that
 *                    agree, and a green tick above it read as "nothing left to
 *                    check" (prod USC-BUG-0005, 0016).
 *   adds-up        — green. The lines come to the printed Grand Total and no
 *                    cell is pink.
 *
 * `null` when the note has nothing to say: no lines, or the arithmetic does not
 * reconcile — the panel below says that instead. A missing Grand Total is grey
 * whether or not a printed subtotal disagrees; both are true at once.
 *
 * Pink is counted the way the grid counts it: `countFlags` for the lines, plus
 * the error-level field flags `reviewInvoice` puts on the header and totals.
 */
export type TotalsNoteTone = "no-grand-total" | "pink-remains" | "adds-up" | "close" | "does-not-add-up";

/**
 * D32 (PROVISIONAL, O-12) narrows green and adds two tones:
 *
 *   close           — amber. Inside the refusal band, max(0.50, 1%), but
 *                     further out than rounding explains (`gap` on `gapOn`,
 *                     from `review.roundingGap`). Sea Foods 128184-12 read
 *                     TT$5.18 of missing tax as "Adds up" because green used
 *                     the refusal band (USC-BUG-0016). Wins over pink-remains.
 *   does-not-add-up — red, where this used to return null. The arithmetic check
 *                     that raises an error failed; the panel below itemises it.
 *
 * Display only: `tolerance()` and everything Confirm & sync refuses are
 * untouched. A blank tax amount beside a rate is pink, so never green; Save's
 * fill of it stays (D39).
 */
export function totalsNote(
  extracted: any,
  review: Pick<InvoiceReview, "computed" | "stated" | "fieldFlags" | "roundingGap">,
  reconciled: boolean
): { tone: TotalsNoteTone; pinkCells: number; gap?: number; gapOn?: string } | null {
  if (!review.computed.lines) return null;
  const ex = extracted || {};
  const lines = Array.isArray(ex.lineItems) ? ex.lineItems : [];
  const pinkCells =
    countFlags(lines, ex.validation).errors +
    Object.values(review.fieldFlags || {}).filter((f) => f.level === "error").length;
  if (review.stated.grandTotal === null) return { tone: "no-grand-total", pinkCells };
  const off = review.roundingGap ? { gap: review.roundingGap.gap, gapOn: review.roundingGap.on } : null;
  if (!reconciled) return { tone: "does-not-add-up", pinkCells, ...off };
  if (off) return { tone: "close", pinkCells, ...off };
  return { tone: pinkCells > 0 ? "pink-remains" : "adds-up", pinkCells };
}

// ── Totals read from the document, or not (VI-3) ─────────────────────────────
//
// The server decides whether each stated total is printed in the OCR text
// (src/utils/printedFigures.js, BUSINESS_RULES#figure-printed-on-document) and
// sends the verdict as `extracted.validation.totalsEvidence`. Nothing here
// judges it again; this only words it.

export type TotalsVerdict = "read" | "not_on_document";

/** The Totals card's rows, in its order. */
export const TOTAL_LABELS: Record<string, string> = {
  subTotal: "SubTotal",
  taxTotal: "Tax Total",
  discountTotal: "Discount",
  shipping: "Shipping",
  grandTotal: "Grand Total",
};

/**
 * The line under a figure on the Totals card. A hand edit outranks the verdict:
 * the figure is then the reviewer's, whatever the document said.
 */
export function totalsEvidenceNote(
  verdict: TotalsVerdict | undefined,
  edited: boolean
): { hint?: string; hintTone?: "warn" } {
  if (edited) return { hint: "Edited by hand" };
  if (verdict === "read") return { hint: "Read from the document" };
  if (verdict === "not_on_document") {
    return { hint: "Not found on the document, may be cut off; check the paper", hintTone: "warn" };
  }
  return {};
}

/**
 * The stated totals the document does not show and nobody has typed in, as the
 * Totals card labels them. While any remain, "Adds up" only proves the lines
 * agree with figures made from those same lines (USC-BUG-0023), not with the paper.
 */
export function uncheckedTotals(validation: any, isEdited: (path: string) => boolean): string[] {
  const evidence = validation?.totalsEvidence || {};
  return Object.keys(TOTAL_LABELS)
    .filter((key) => evidence[key] === "not_on_document" && !isEdited(`extracted.totals.${key}`))
    .map((key) => TOTAL_LABELS[key]);
}

export type InvoiceReview = {
  /**
   * The largest gap beyond TRUE rounding — 0.005 per cent-rounded figure summed,
   * plus 0.01 — across the same comparisons the arithmetic checks make, e.g.
   * `{ gap: 5.18, on: "the tax total" }`. `null` when all are within rounding
   * (D32). Says nothing about refusal; `issues` does that.
   */
  roundingGap: { gap: number; on: string } | null;
  issues: InvoiceIssue[];
  /** Keyed by the KVTable row path, e.g. "extracted.totals.grandTotal". */
  fieldFlags: Record<string, CellFlag>;
  /** What the lines actually come to, whatever the foot of the invoice says. */
  /** `vatInLineTotals`: the lines already hold the foot's VAT (invoiceLineMath.linesHoldFootVat). */
  computed: { subTotal: number; taxTotal: number; grandTotal: number; lines: number; vatInLineTotals: boolean };
  /**
   * What the foot of the invoice says, read through the same variant-tolerant
   * lookup the reconciliation uses. `null` where the document states nothing.
   * Exposed so the screen can say the two AGREE, not only that they differ:
   * the panel below is raised on `issues`, so when an invoice reconciles it
   * simply disappears, and silence was the only signal a reviewer got that
   * they were clear to confirm.
   */
  stated: { subTotal: number | null; taxTotal: number | null; grandTotal: number | null };
  /**
   * Freight / invoice-level discount, and what we make of them.
   *
   * `addenda` is the net figure the grand-total checks actually applied — 0
   * where a freight was judged impossible, so a screen quoting this is quoting
   * the arithmetic that ran. `suspect` is non-null when the freight needs a
   * person to look at the document.
   */
  addenda: {
    net: number;
    shipping: number | null;
    discountTotal: number | null;
    suspect: AddendaSuspect | null;
  };
  /**
   * Money the supplier has asked for beyond what their own quantities and
   * prices come to. The number to put in front of a person before they pay.
   */
  overcharge: {
    total: number;
    lines: { lineNo: any; itemName: string; expected: number; charged: number; difference: number }[];
    convention: Convention;
  };
};

/** "page 2", "pages 1–3", or "pages 1, 3" when the pages are not one run; "" for no pages. */
export function describePages(pages: number[]): string {
  const p = [...pages].sort((a, b) => a - b);
  if (p.length === 0) return "";
  if (p.length === 1) return `page ${p[0]}`;
  const run = p.every((n, i) => i === 0 || n === p[i - 1] + 1);
  return run ? `pages ${p[0]}–${p[p.length - 1]}` : `pages ${p.join(", ")}`;
}

/**
 * D94: the page-level facts about WHICH pages of the upload this record was read
 * from, lifted off the stored record into `reviewInvoice`'s context.
 */
export function pageContextOf(inv: { meta?: any; source?: { pages?: number[] } | null } | null | undefined) {
  return {
    pageClassificationNote: typeof inv?.meta?.pageClassificationNote === "string" ? inv.meta.pageClassificationNote : null,
    sourcePages: Array.isArray(inv?.source?.pages) ? inv!.source!.pages! : null,
  };
}

/**
 * Reconcile the whole invoice against its own lines, live.
 *
 * The server does this once at processing time and stores the verdict; this is
 * the same arithmetic against the values on screen. Without it a user could put
 * 10000 in a quantity column, watch the line go red, and still see a totals
 * panel insisting everything reconciled — because it was describing the
 * document as extracted an hour earlier.
 *
 * `context.uploadedAt` is the invoice's upload date (`createdAt`), which the
 * invoice date's year is checked against (D34); `context.now` is the clock,
 * passed by tests.
 */
export function reviewInvoice(
  extracted: any,
  context: {
    uploadedAt?: unknown;
    now?: unknown;
    documentNote?: string | null;
    /** D94: `meta.pageClassificationNote` - the pages could not be checked. */
    pageClassificationNote?: string | null;
    /** D94: `source.pages` - non-empty on a record split off a multi-invoice file. */
    sourcePages?: number[] | null;
    /** D95: false for a company expense - it stocks nothing, so no stock-cell findings. */
    stocks?: boolean;
  } = {}
): InvoiceReview {
  const ex = extracted || {};
  const lines: any[] = Array.isArray(ex.lineItems) ? ex.lineItems : [];
  const totals = ex.totals || {};
  const invoice = ex.invoice || {};

  const issues: InvoiceIssue[] = [];
  const fieldFlags: Record<string, CellFlag> = {};

  // One shared reconciliation, identical to the one the server stores — see
  // invoiceLineMath. `convention` is which form this supplier prints its
  // extensions in, decided by the lines that do reconcile, so the odd one out is
  // measured against the invoice's own habit rather than against a guess.
  const r = reconcileInvoiceLines(ex);
  const { convention } = r;

  // Basis mismatches are excluded from overchargeTotal (invoiceLineMath
  // moneyGaps), so they must be excluded from the lines that DESCRIBE it —
  // otherwise the count and the "worst line" name disagree with the figure in
  // the title, and can point the reviewer at a supplier who billed correctly.
  const overcharged: InvoiceReview["overcharge"]["lines"] = r.discrepancies
    .filter((d) => !d.pricePerItem)
    .map((d) => ({
      lineNo: d.lineNo,
      itemName: d.description,
      expected: d.expected,
      charged: d.actual,
      difference: d.difference,
    }));

  const computed = {
    subTotal: r.computedSubTotal,
    taxTotal: r.computedTaxTotal,
    grandTotal: r.computedGrandTotal,
    lines: lines.length,
    vatInLineTotals: r.vatInLineTotals,
  };

  // Overcharges first: this is the finding that costs real money, and it should
  // lead the panel rather than sit under three arithmetic notes.
  const overchargeTotal = r.overchargeTotal;
  const underchargeTotal = r.underchargeTotal;

  if (overchargeTotal > 0) {
    const worst = overcharged
      .filter((l) => l.difference > 0)
      .sort((a, b) => b.difference - a.difference)[0];
    issues.push({
      level: "error",
      kind: "pricing",
      title: `Overcharged by ${money(overchargeTotal)}`,
      message:
        `${overcharged.filter((l) => l.difference > 0).length} line(s) are billed above what this ` +
        `invoice's own quantity x unit price comes to — worst is line ${worst.lineNo} ` +
        `(${worst.itemName}), charged ${money(worst.charged)} where ${money(worst.expected)} is due. ` +
        `Check before paying.`,
    });
  }
  if (underchargeTotal > 0) {
    issues.push({
      level: "warn",
      kind: "pricing",
      title: `Undercharged by ${money(underchargeTotal)}`,
      message:
        `${overcharged.filter((l) => l.difference < 0).length} line(s) are billed below what the ` +
        `quantity and unit price come to. In your favour, but the supplier may re-invoice.`,
    });
  }

  const { subTotal: statedSub, taxTotal: statedTax, grandTotal: statedGrand } = r.stated;

  // Freight adds to the grand total and an invoice-level discount subtracts
  // from it, so no comparison against the printed grand total holds without
  // them. This screen used to leave both out while the server's
  // computeLineValidation put both in — two formulas answering the same
  // question, and the grid was the one people read. An invoice with genuine
  // freight showed a false "does not add up" here and passed there; an invoice
  // with a phantom freight showed nothing here at all.
  //
  // auditAddenda also judges the freight: one it can prove the invoice has no
  // room for is dropped from the sum and flagged instead of quietly shifting
  // every total below by its own size.
  const addendaAudit = auditAddenda(r.stated);
  const addenda = addendaAudit.addenda;
  const withAddenda = (n: number) => round2(n + addenda);
  /** " + 4.00 freight" — named in the message, so the sum can be followed. */
  const addendaNote = addenda
    ? ` ${addenda > 0 ? "+" : "−"} ${money(Math.abs(addenda))} freight/discount`
    : "";

  if (addendaAudit.suspect) {
    // Amber, never red: this is a figure to check against the paper, not a
    // proven error. On the `occupies-tax` shape the arithmetic reconciles
    // perfectly and the only thing wrong is which box the money sits in.
    fieldFlags["extracted.totals.shipping"] = {
      level: "warn",
      reason: addendaAudit.suspect.reason,
    };
    if (addendaAudit.suspect.code === "occupies-tax") {
      // The other half of the same suspicion. Tax reading 0 is the visible
      // symptom — a reclaim that will not be made — so it gets said on the
      // field that is wrong, not only on the one holding the stray number.
      fieldFlags["extracted.totals.taxTotal"] = {
        level: "warn",
        reason: addendaAudit.suspect.reason,
      };
    }
    issues.push({
      level: "warn",
      kind: "freight",
      title: "Freight on this invoice does not look real",
      message: addendaAudit.suspect.reason,
    });
  }

  if (lines.length) {
    if (statedSub !== null && Math.abs(computed.subTotal - statedSub) > tolerance(statedSub)) {
      const message =
        `The lines add up to ${money(computed.subTotal)} before tax, but the invoice's ` +
        `subtotal says ${money(statedSub)}.`;
      issues.push({ level: "error", kind: "arithmetic", title: "Lines do not match the subtotal", message });
      fieldFlags["extracted.totals.subTotal"] = { level: "error", reason: message };
    }

    if (
      statedGrand !== null &&
      Math.abs(withAddenda(computed.grandTotal) - statedGrand) > tolerance(statedGrand)
    ) {
      const message =
        `The lines add up to ${money(computed.grandTotal)} including tax${addendaNote}, but the ` +
        `invoice's grand total says ${money(statedGrand)}.`;
      issues.push({ level: "error", kind: "arithmetic", title: "Lines do not match the grand total", message });
      fieldFlags["extracted.totals.grandTotal"] = { level: "error", reason: message };
    }

    // Only when the lines actually SAY something about tax. When none of them
    // do, VAT was added once at the foot and never printed per line — the
    // ordinary Trinidad shape — and comparing 0 against the printed total
    // flagged every correctly-read invoice of that kind.
    if (
      r.anyLineStatesTax &&
      statedTax !== null &&
      Math.abs(computed.taxTotal - statedTax) > tolerance(statedTax)
    ) {
      const message =
        `The lines carry ${money(computed.taxTotal)} of tax, but the invoice's tax total ` +
        `says ${money(statedTax)}.`;
      issues.push({ level: "error", kind: "arithmetic", title: "Line tax does not match the tax total", message });
      fieldFlags["extracted.totals.taxTotal"] = { level: "error", reason: message };
    }
  }

  if (statedSub !== null && statedTax !== null && statedGrand !== null) {
    const expected = withAddenda(round2(statedSub + statedTax));
    if (Math.abs(expected - statedGrand) > tolerance(statedGrand)) {
      const message =
        `Subtotal ${money(statedSub)} + tax ${money(statedTax)}${addendaNote} = ${money(expected)}, ` +
        `but the grand total says ${money(statedGrand)}.`;
      issues.push({ level: "error", kind: "arithmetic", title: "The invoice's own totals do not add up", message });
      fieldFlags["extracted.totals.grandTotal"] = { level: "error", reason: message };
    }
  }

  // --- Lines that describe a pack but do not say how big it is, or say it
  // wrongly (D65) --------------------------------------------------------
  // The same question the server's Confirm & sync gate asks, from the same
  // `missingStockCells` in the mirrored twin — asked HERE so the reviewer is
  // told before they click rather than by a refusal afterwards.
  //
  // It is raised as an ISSUE and not only as a red cell because `issues` is
  // what the button reads: per-cell flags never reach it, which is why a line
  // could go red in the grid and sync anyway (TECH_DEBT #86).
  //
  // "${label}: ${reason}", not "${label} is empty — ${reason}" — D65 widened
  // this rule to cells that are NOT empty at all (a locked unit holding more
  // than one, a pack of one with no proof, a size typed into the count), and
  // "Units / Case is empty" would be a false statement about a cell that
  // plainly holds a number. Every `reason` here is written to stand on its
  // own after the label, whichever of the four rules produced it.
  // D95: a company expense stocks nothing, so it describes no pack.
  const packless = (context.stocks === false ? [] : lines)
    .map((li, i) => ({ lineNo: li?.lineNo ?? i + 1, cells: missingStockCells(li) }))
    .filter((row) => row.cells.length > 0);
  if (packless.length) {
    issues.push({
      level: "error",
      kind: "stock",
      title: `${packless.length} line${packless.length === 1 ? " does" : "s do"} not say how big the pack is`,
      message: packless
        .map((row) => `Line ${row.lineNo}: ${row.cells.map((c) => `${c.label}: ${c.reason}`).join("; ")}`)
        .join("\n"),
    });
  }

  // --- Lines against the paper (VI-2, PROVISIONAL O-16: warn only) -----------
  // The server judges the OCR rows (src/utils/printedFigures.js lineAlignment)
  // and both reads judge them again, so the stored positions match these lines.
  // `warn`, so none of these reaches the Confirm & sync refusal.
  const lineName = (li: any, i: number) =>
    `Line ${li?.lineNo ?? i + 1}` +
    (!isEmptyValue(li?.itemName) || !isEmptyValue(li?.description)
      ? ` (${String(!isEmptyValue(li?.itemName) ? li.itemName : li.description).trim()})`
      : !isEmptyValue(li?.itemCode)
      ? ` (code ${String(li.itemCode).trim()})`
      : "");
  const validation = ex.validation || {};
  const misaligned = (Array.isArray(validation.lineAlignmentFlags) ? validation.lineAlignmentFlags : [])
    .map((f: any) => f?.index)
    .filter((i: any) => Number.isInteger(i) && i >= 0 && i < lines.length);
  if (misaligned.length) {
    issues.push({
      level: "warn",
      kind: "document",
      title: `${misaligned.length} line${misaligned.length === 1 ? "" : "s"} may sit on the wrong printed row`,
      message:
        misaligned
          .map((i: number) => `${lineName(lines[i], i)}: its price and total are printed beside a different product.`)
          .join("\n") +
        "\nCheck the paper. A vessel name or heading read as a line pushes every name one row up; " +
        "remove that line and correct the names.",
    });
  }
  const printedRows = num(validation.printedRowsFlag?.printedRows);
  if (printedRows !== null && printedRows > lines.length) {
    issues.push({
      level: "warn",
      kind: "document",
      title: `The document prints ${printedRows} priced rows, but ${lines.length} line${
        lines.length === 1 ? " was" : "s were"
      } read`,
      message: "A product may have been left out, often the last row of the table. Compare the lines with the paper.",
    });
  }
  // USC-BUG-0021: a "collecting" row read as a line, code *800-000-0000 and
  // nothing else. Not a product, and nothing said it could be removed.
  const removable = lines
    .map((li, i) => ({ li, i }))
    .filter(
      ({ li }) =>
        isEmptyValue(li?.itemName) && isEmptyValue(li?.description) && !num(li?.unitPrice) && !num(li?.lineTotal)
    );
  if (removable.length) {
    issues.push({
      level: "warn",
      kind: "document",
      title: `${removable.length} line${removable.length === 1 ? " has" : "s have"} no name and no price`,
      message:
        removable.map(({ li, i }) => lineName(li, i)).join(", ") +
        ": if it is not a product (a note such as \"collecting\"), use Remove on its row before Confirm & sync.",
    });
  }

  // --- Pages of one upload (VI-4, PROVISIONAL O-18: warn only) ---------------
  // Several photos or files are joined into ONE invoice, so two invoices can be
  // joined by mistake. Both reads judge each page's labelled invoice number
  // (src/utils/printedFigures.js pageInvoiceNumbers).
  const pageNumbers: any[] = Array.isArray(validation.pageInvoiceNumbersFlag?.pages)
    ? validation.pageInvoiceNumbersFlag.pages
    : [];
  if (pageNumbers.length > 1) {
    issues.push({
      level: "warn",
      kind: "document",
      title: "The pages show different invoice numbers",
      message:
        pageNumbers.map((p: any) => `Page ${p?.page}: ${p?.invoiceNumber}`).join("\n") +
        "\nThese may be two invoices joined by mistake. They are being processed as ONE invoice. " +
        "If they are separate, delete this upload and upload each invoice on its own.",
    });
  }
  // Over the page attach limit, or when the layout could not be sent, the
  // document is read from its OCR text alone (vendorInvoiceExtractionV2
  // buildDocumentAttachment). The extractor's own note says why.
  if (typeof context.documentNote === "string" && context.documentNote.trim()) {
    issues.push({
      level: "warn",
      kind: "document",
      title: "Read from the text only, without the page layout",
      message: `${context.documentNote.trim()}. Columns and per-line VAT are less reliable read this way; check each line against the paper.`,
    });
  }

  // --- Pages that are not this invoice (D94) -----------------------------------
  // A purchase order or cheque voucher stapled to the invoice is skipped; the
  // invoice is read from its own pages, and the reviewer is told which were left out.
  const skipped: { page: number; type: string }[] = Array.isArray(ex.skippedPages) ? ex.skippedPages : [];
  if (skipped.length) {
    const what = (t: string) => (t === "purchase_order" ? "a purchase order" : t === "cheque_voucher" ? "a cheque voucher" : "another document");
    const parts = skipped.map((p, i) => (i === 0 ? `Page ${p.page} is ${what(p.type)}` : `page ${p.page} ${what(p.type)}`));
    const said = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0];
    issues.push({
      level: "warn",
      kind: "document",
      title: "Some pages were not read",
      message: `${said} — not read.`,
    });
  }
  if (typeof context.pageClassificationNote === "string" && context.pageClassificationNote.trim()) {
    issues.push({
      level: "warn",
      kind: "document",
      title: "The pages could not be checked",
      message: context.pageClassificationNote.trim(),
    });
  }
  const own = Array.isArray(context.sourcePages) ? [...context.sourcePages].sort((a, b) => a - b) : [];
  if (own.length) {
    issues.push({
      level: "warn",
      kind: "document",
      title: "Split from a file with several invoices",
      message: `Read from ${describePages(own)} of a file that held several invoices.`,
    });
  }

  // --- Header fields ---------------------------------------------------------
  if (isEmptyValue(invoice.invoiceNumber)) {
    fieldFlags["extracted.invoice.invoiceNumber"] = {
      level: "error",
      reason: "Invoice Number is empty — payables cannot be matched to this document without it.",
    };
  }
  if (isEmptyValue(invoice.invoiceDate)) {
    fieldFlags["extracted.invoice.invoiceDate"] = {
      level: "error",
      reason: "Invoice Date is empty — the due date and the ageing of this payable both depend on it.",
    };
  }
  if (isEmptyValue(totals.grandTotal)) {
    fieldFlags["extracted.totals.grandTotal"] = {
      level: "error",
      reason: "Grand Total is empty — there is nothing to pay against.",
    };
  }
  if (isEmptyValue(ex.vendor?.name)) {
    fieldFlags["extracted.vendor.name"] = {
      level: "error",
      reason: "Vendor Name is empty — this invoice cannot be attributed to a supplier.",
    };
  }

  if (isEmptyValue(invoice.dueDate)) {
    const derived = deriveDueDate(invoice.invoiceDate, invoice.paymentTerms);
    fieldFlags["extracted.invoice.dueDate"] = {
      level: "warn",
      reason: derived
        ? `Due Date is empty. ${derived.note} Expected ${derived.dueDate}.`
        : isEmptyValue(invoice.paymentTerms)
        ? "Due Date is empty, and there are no payment terms to calculate it from. Enter the terms (e.g. \"30 days\") and the date will be filled in."
        : `Due Date is empty and the payment terms ("${invoice.paymentTerms}") do not state a period we can calculate from. Enter the date directly.`,
    };
  }

  // Dates that read, but not as a real invoice would (D34, PROVISIONAL): more
  // than 3 days ahead, a year that is not the upload year, or due before it was
  // issued. Amber, and a cell flag on purpose rather than an issue: `issues` is
  // what Confirm & sync reads, and these are questions, not refusals. Each
  // needs its dates readable, so it never lands on a cell the empty flags hold.
  const dates = invoiceDateChecks({
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    uploadedAt: context.uploadedAt,
    now: context.now,
  });
  const dateDoubts: string[] = [];
  if (dates.future) dateDoubts.push(`is more than ${FUTURE_DATE_TOLERANCE_DAYS} days in the future`);
  if (dates.notUploadYear) {
    dateDoubts.push(
      `is in ${toInvoiceDate(invoice.invoiceDate)?.getUTCFullYear()}, but the invoice was uploaded in ${toInvoiceDate(context.uploadedAt)?.getUTCFullYear()}`
    );
  }
  // Each date is also spelled out ("8 November 2026"): a supplier printing
  // month-first prints the same digits the other way round, so dd/mm/yyyy alone
  // does not say which day USC read (USC-BUG-0055).
  const readAs = (value: unknown) => `${formatDdMmYyyy(value)} (${formatDateSpelled(value)})`;
  if (dateDoubts.length) {
    fieldFlags["extracted.invoice.invoiceDate"] = {
      level: "warn",
      reason: `Invoice Date ${readAs(invoice.invoiceDate)} ${dateDoubts.join(" and ")}. Check it against the document: the due date and the catalogue's newest price both go by this date.`,
    };
  }
  if (dates.dueBeforeInvoice) {
    fieldFlags["extracted.invoice.dueDate"] = {
      level: "warn",
      reason: `Due Date ${readAs(invoice.dueDate)} is before the Invoice Date ${readAs(invoice.invoiceDate)}. One of them is misread; check both against the document.`,
    };
  }

  if (isEmptyValue(invoice.paymentTerms)) {
    fieldFlags["extracted.invoice.paymentTerms"] = {
      level: "warn",
      reason: "Payment Terms are empty — without them the due date cannot be calculated.",
    };
  }
  if (invoice.currencyConfident === false) {
    fieldFlags["extracted.invoice.currencyCode"] = {
      level: "warn",
      reason: invoice.currencyNote || "The currency could not be resolved from the invoice. Confirm it.",
    };
  }

  // --- D32: off by more than rounding? (PROVISIONAL, O-12) --------------------
  // The comparisons the arithmetic checks above make, asked stricter. Those run
  // at `tolerance()` and are what Confirm & sync refuses on; D32 leaves them
  // exactly as they are. Each cent-rounded figure in a sum can be half a cent
  // out, so rounding explains at most 0.005 per figure plus a cent for the total.
  // ponytail: one figure per line per sum. A VAT-inclusive line really carries
  // two (printed total less our tax), so a long VAT-inclusive invoice can read
  // amber by a cent; count per-line figures if that is ever seen.
  const addendaFigures =
    (addendaAudit.shipping !== null ? 1 : 0) + (addendaAudit.discountTotal !== null ? 1 : 0);
  const gaps: { on: string; gap: number; allowed: number }[] = [];
  const measure = (on: string, lhs: number, rhs: number, figures: number) =>
    gaps.push({ on, gap: round2(Math.abs(lhs - rhs)), allowed: 0.005 * figures + 0.01 });
  const n = lines.length;
  if (n) {
    if (statedSub !== null) measure("the subtotal", computed.subTotal, statedSub, n);
    if (statedGrand !== null) {
      const taxFigures = r.anyLineStatesTax ? n : statedTax !== null ? 1 : 0;
      measure("the grand total", withAddenda(computed.grandTotal), statedGrand, n + taxFigures + addendaFigures);
    }
    if (r.anyLineStatesTax && statedTax !== null) measure("the tax total", computed.taxTotal, statedTax, n);
  }
  if (statedSub !== null && statedTax !== null && statedGrand !== null) {
    measure("the invoice's own totals", withAddenda(round2(statedSub + statedTax)), statedGrand, 2 + addendaFigures);
  }
  // 1e-9: gaps are whole cents and a band ends on a half cent or a whole one.
  const worst = gaps.filter((g) => g.gap > g.allowed + 1e-9).sort((a, b) => b.gap - a.gap)[0];
  const roundingGap = worst ? { gap: worst.gap, on: worst.on } : null;

  return {
    roundingGap,
    issues,
    fieldFlags,
    computed,
    stated: { subTotal: statedSub, taxTotal: statedTax, grandTotal: statedGrand },
    addenda: {
      net: addenda,
      shipping: addendaAudit.shipping,
      discountTotal: addendaAudit.discountTotal,
      suspect: addendaAudit.suspect,
    },
    overcharge: { total: overchargeTotal, lines: overcharged, convention },
  };
}
